import { useContext, useEffect, useState } from 'react';

import { FilterOptions, Sort } from '@vimeo/comments-ui';
import { PostAlbumVideoCommentRepliesFields } from '@vimeo/gctl-api/fetchers/album/video/comment/replies';
import { PostVideoCommentRepliesFields } from '@vimeo/gctl-api/fetchers/video/comment/replies';
import { usePostAlbumVideoCommentReplies } from '@vimeo/gctl-api/hooks/useAlbumVideoCommentReplies';
import { usePostVideoCommentReplies } from '@vimeo/gctl-api/hooks/useVideoCommentReplies';
import { usePostVideoPrivateCommentReplies } from '@vimeo/gctl-api/hooks/useVideoPrivateCommentReplies';
import { Comment } from '@vimeo/gctl-api/models/components/schemas/comment/Comment';
import { translate } from '@vimeo/i18n-runtime';
import { getPasswordHashFromCookie } from '@vimeo/use-unlocked-video';

import { AnalyticsPropsType } from '../../CommentsCommon/types';
import { bpPostComment } from '../../utils/analytics';
import { POST_PUBLIC_REPLY_FIELDS } from '../../utils/constants';
import {
  checkForMention,
  getReviewPasswordHashFromCookie,
  getShowcasePasswordHashFromCookie,
  getVideoIdFromClipRequestId,
  idFromUri,
} from '../../utils/helper';
import { guestLoginModalContext } from '../use-guestlogin-modal';
import { useLoginModal } from '../use-login-modal';
import { useVideoData } from '../use-video-data';
import { useAuthenticatedViewer } from '../use-viewer';
import { usePrivateComments, usePublicComments } from './use-get-comments';

/**
 * A customized hook to handle posting Public and Private comments data depending on privacy
 */
export function usePostReply(
  clipRequestId: string,
  isPublic: boolean,
  sort?: Sort,
  analyticsProps?: AnalyticsPropsType,
  filterOptions?: FilterOptions,
  showcaseId?: number,
  reviewId?: string,
) {
  const usePostReplyHook = isPublic ? usePostPublicReply : usePostPrivateReply;
  return usePostReplyHook(clipRequestId, sort, analyticsProps, filterOptions, showcaseId, reviewId);
}

/**
 * A hook to handle posting Private comments data
 */
export function usePostPrivateReply(
  clipRequestId: string,
  sort?: Sort,
  analyticsProps?: AnalyticsPropsType,
  filterOptions?: FilterOptions,
  showcaseId?: number,
  reviewId?: string,
) {
  const loggedInViewer = useAuthenticatedViewer();
  const videoId = getVideoIdFromClipRequestId(clipRequestId);
  const [activeUri, setActiveUri] = useState<string | null>(null);
  const [postVideoPrivateReplies, { data: newReply, loading, error: postReplyError }] =
    usePostVideoPrivateCommentReplies();
  const { revalidateComments } = usePrivateComments(
    clipRequestId,
    sort,
    filterOptions,
    undefined,
    reviewId,
  );
  const { videoData } = useVideoData(clipRequestId, undefined, reviewId);
  const { toggleGuestLoginModal, guestUser } = useContext(guestLoginModalContext);
  const isLoggedIn = loggedInViewer || !!guestUser;
  const password = getReviewPasswordHashFromCookie(reviewId);
  const guestUserName = guestUser ? JSON.parse(guestUser)?.guestUserName : null;
  const guestUserId = guestUser ? JSON.parse(guestUser)?.guestUserId : null;

  const post = async (text: string, uri: string, userName?: string) => {
    if (!isLoggedIn) {
      toggleGuestLoginModal(true);
      return;
    }

    const name = userName || loggedInViewer?.user?.name || guestUserName;
    setActiveUri(uri);
    await postVideoPrivateReplies({
      where: {
        videoId: clipRequestId as unknown as number,
        commentId: idFromUri(uri),
      },
      select: [
        'text',
        'richtext',
        'uri',
        'metadata.connections.user',
        'createdTime',
        'metadata',
        'timeCode',
        'userName',
      ],
      // @ts-expect-error controller annotations are not upto date, text is mandatory.
      variables: { richtext: text, name, guestUserId },
      query: { reviewId, password },
    });
  };

  useEffect(() => {
    if (!loading && !postReplyError && newReply && activeUri) {
      const uploaderId = Number((videoData?.uploader?.link || '').match(/user(\d+)/)?.[1] || 0);
      const videoPrivacy = videoData?.privacy.view;
      revalidateComments((data) => {
        const filteredData = data
          ?.filter((page) => !!page)
          .map((set) => {
            return {
              ...set,
              data: set.data?.map((comment) => {
                if (comment.uri === activeUri) {
                  if (analyticsProps) {
                    const isMention = checkForMention(newReply?.richtext || newReply?.text || '');
                    let analytics = {
                      ...analyticsProps,
                      commentContextFields: {
                        comment_id: idFromUri(newReply.uri) as number,
                        is_reply: true,
                        comment_owner_id: (loggedInViewer?.user?.id as number) ?? -1,
                        comment_length: newReply.text.length,
                        is_mention: isMention,
                        existing_reply_counts: comment.replies.length,
                      },
                    };
                    bpPostComment(
                      videoId,
                      videoPrivacy,
                      uploaderId,
                      analytics,
                      loggedInViewer,
                      false,
                      false,
                    );
                  }
                  comment.replies = [...comment.replies, newReply];
                  if (comment.status === 'closed') {
                    comment.status = 'open';
                  }
                }
                return comment;
              }),
              total: set.total ? set.total + 1 : 0,
            };
          });
        return filteredData;
      });

      setActiveUri(null);
    }
  }, [
    newReply,
    postReplyError,
    loading,
    revalidateComments,
    activeUri,
    videoData?.uploader?.link,
    videoData?.privacy.view,
    videoId,
    loggedInViewer,
    analyticsProps,
  ]);

  return {
    postReply: post,
    postReplyLoading: loading,
    postReplyError: !!postReplyError
      ? { errorMessage: translate('Unable to post reply'), errorCommentId: activeUri }
      : null,
  };
}

/**
 * A hook to handle posting Public comments data
 */
export function usePostPublicReply(
  clipRequestId: string,
  sort?: Sort,
  analyticsProps?: AnalyticsPropsType,
  filterOptions?: FilterOptions,
  showcaseId?: number,
) {
  const videoId = getVideoIdFromClipRequestId(clipRequestId);
  const { revalidateComments } = usePublicComments(clipRequestId, sort, filterOptions, showcaseId);
  const [postPublicReply, replyState] = usePostVideoCommentReplies();
  const [postAlbumPublicReply, albumReplyState] = usePostAlbumVideoCommentReplies();
  const {
    data: newReply,
    loading,
    error: postReplyError,
  } = showcaseId ? albumReplyState : replyState;
  const { videoData } = useVideoData(clipRequestId, showcaseId);
  const videoPrivacy = videoData?.privacy.view;
  const loggedInViewer = useAuthenticatedViewer();
  const { toggleGuestLoginModal, guestUser } = useContext(guestLoginModalContext);
  const isLoggedIn = loggedInViewer || !!guestUser;
  const { toggleLoginModal, setRedirectUrl } = useLoginModal();

  const password = getPasswordHashFromCookie(clipRequestId);

  const [activeUri, setActiveUri] = useState<string | null>(null);

  const post = async (text: string, uri: string) => {
    // Check if user is logged out and show login modal
    if (!isLoggedIn && videoPrivacy === 'unlisted') {
      toggleGuestLoginModal(true);
      return;
    } else if (!isLoggedIn) {
      setRedirectUrl(window.location.pathname + window.location.search);
      toggleLoginModal(true);
      return;
    }
    const replyText = { richtext: text };
    // If logged in, post the reply
    setActiveUri(uri);
    if (showcaseId) {
      const showcasePassword = getShowcasePasswordHashFromCookie(showcaseId);
      await postAlbumPublicReply({
        where: {
          albumId: showcaseId,
          videoId: clipRequestId as unknown as number,
          commentId: idFromUri(uri),
        },
        select: POST_PUBLIC_REPLY_FIELDS as PostAlbumVideoCommentRepliesFields,
        // @ts-expect-error controller annotations are not upto date, text is mandatory.
        variables: replyText,
        ...(showcasePassword ? { query: { password: showcasePassword } } : {}),
      });
    } else {
      await postPublicReply({
        where: { videoId: clipRequestId as unknown as number, commentId: idFromUri(uri) },
        select: POST_PUBLIC_REPLY_FIELDS as PostVideoCommentRepliesFields,
        // @ts-expect-error controller annotations are not upto date, text is mandatory.
        variables: replyText,
        ...(password ? { query: { password } } : {}),
      });
    }
  };
  useEffect(() => {
    if (!loading && !postReplyError && newReply && activeUri) {
      const uploaderId = Number((videoData?.uploader?.link || '').match(/user(\d+)/)?.[1] || 0);
      revalidateComments((data) => {
        const filteredData = data
          ?.filter((page) => !!page)
          .map((set) => {
            return {
              ...set,
              data: set.data?.map((comment: Comment) => {
                if (comment.uri === activeUri) {
                  const isMention = checkForMention(newReply.text || '');
                  if (analyticsProps) {
                    let analytics = {
                      ...analyticsProps,
                      commentContextFields: {
                        comment_id: idFromUri(newReply.uri) as number,
                        is_reply: true,
                        comment_owner_id: (loggedInViewer?.user?.id as number) ?? -1,
                        comment_length: newReply.text.length,
                        is_mention: isMention,
                        existing_reply_counts: comment.replies.length,
                      },
                    };
                    bpPostComment(videoId, videoPrivacy, uploaderId, analytics, loggedInViewer);
                  }
                  comment.replies = [...comment.replies, newReply];
                }
                return comment;
              }),
              total: set.total ? set.total + 1 : 0,
            };
          });
        return filteredData;
      });
      setActiveUri(null);
    }
  }, [
    newReply,
    loading,
    revalidateComments,
    activeUri,
    postReplyError,
    videoData?.uploader?.link,
    videoData?.privacy.view,
    videoId,
    loggedInViewer,
    analyticsProps,
    videoPrivacy,
  ]);

  return {
    postReply: post,
    postReplyLoading: loading,
    postReplyError: !!postReplyError
      ? { errorMessage: translate('Unable to post reply'), errorCommentId: activeUri }
      : null,
  };
}
